/* ==========================================================================
   OTX PRICE FACET -- dual-handle range slider (v3)

   WHY v3: v2 drew two stacked native <input type="range"> elements and relied
   on pointer-events:none on the input / auto on the ::-webkit-slider-thumb to
   let you grab either one. That is fragile -- the top input swallows the drag
   whenever the two handles overlap or a theme stylesheet touches
   input[type=range], which is what made the bar look dead. v3 draws its own
   thumbs and drives them with pointer events + setPointerCapture, so the hit
   target is explicit and nothing can sit on top of it.

   The native gte/lte inputs are still NOT removed, only hidden: Dawn's
   FacetFiltersForm reads them to build the filter URL. The slider writes into
   them on release and fires a bubbling `input` event, which Dawn already
   listens for on the facet form and debounces.

   READ THIS BEFORE EDITING -- snippets/price-facet.liquid is not stock-Dawn
   shaped:
     - inputs are type="text", not type="number"
     - there is NO min/max attribute. Bounds live in data-min / data-max, and
       the ceiling is ALSO the max input's placeholder
     - the currency span is .field-currency (single hyphen)
     - values come through money_without_currency, so they can carry commas
       ("1,299.00") and must be de-comma'd before parseFloat

   The hide rule uses a DESCENDANT selector, not a child selector. v2 used
   `> .field`, which silently fails if the theme wraps price-facet's output in
   an extra element -- the native text boxes then stay visible under the bar.

   facets.js replaces the whole facet panel after each filter change, so init
   re-runs on a MutationObserver. Idempotent via data-otx-range.
   ========================================================================== */
(function () {
  'use strict';

  var STYLE_ID = 'otx-range-style';

  var CSS = [
    '.facets__price[data-otx-range] .field,',
    '.facets__price[data-otx-range] .field-currency,',
    '.facets__price[data-otx-range] .field__currency { display: none !important; }',
    '.facets__price[data-otx-range] { display: block !important; padding: 10px 0 22px !important; }',
    '.otx-range { display: block; position: relative; }',
    '.otx-range__readout { display: flex; justify-content: space-between; align-items: baseline;',
    '  font-family: "Neue Haas Unica Pro Heavy", sans-serif; font-weight: 800; font-size: 13px;',
    '  letter-spacing: .04em; color: #ffffff; margin: 0 0 18px; }',
    '.otx-range__readout em { font-style: normal; color: rgba(255,255,255,.35); padding: 0 6px; }',
    '.otx-range__track { position: relative; height: 24px; margin: 0 9px; touch-action: none;',
    '  cursor: pointer; }',
    '.otx-range__rail { position: absolute; top: 11px; left: 0; right: 0; height: 2px;',
    '  background: rgba(255,255,255,.22); }',
    '.otx-range__fill { position: absolute; top: 11px; height: 2px; background: #d21c1c; }',
    '.otx-range__thumb { position: absolute; top: 4px; width: 16px; height: 16px; margin-left: -8px;',
    '  border-radius: 50%; background: #ffffff; border: 3px solid #d21c1c; box-sizing: border-box;',
    '  cursor: grab; touch-action: none; }',
    '.otx-range__thumb:active, .otx-range[data-dragging] .otx-range__thumb { cursor: grabbing; }',
    '.otx-range__thumb:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(210,28,28,.45); }'
  ].join('\n');

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  /* money_without_currency can emit "1,299.00" */
  function num(raw) {
    if (raw === null || raw === undefined || raw === '') return NaN;
    return parseFloat(String(raw).replace(/,/g, ''));
  }

  /* first argument that actually parses */
  function firstNum() {
    for (var i = 0; i < arguments.length; i++) {
      var v = num(arguments[i]);
      if (!isNaN(v)) return v;
    }
    return NaN;
  }

  function build(container) {
    if (container.hasAttribute('data-otx-range')) return;

    var gte = container.querySelector('input[name$=".price.gte"]');
    var lte = container.querySelector('input[name$=".price.lte"]');
    if (!gte || !lte) return;

    var floor = firstNum(gte.getAttribute('data-min'), 0);
    var rawCeiling = firstNum(
      lte.getAttribute('data-max'),
      gte.getAttribute('data-max'),
      lte.getAttribute('placeholder'),
      lte.max
    );
    if (isNaN(floor)) floor = 0;
    if (isNaN(rawCeiling) || rawCeiling <= floor) return;

    /* whole dollars on the handles; round up so the top-priced item stays reachable */
    var ceiling = Math.ceil(rawCeiling);
    var span = ceiling - floor;

    var symbolEl = container.querySelector('.field-currency, .field__currency');
    var symbol = symbolEl ? symbolEl.textContent.trim() : '$';

    /* current selection, if the shopper already filtered */
    var low = firstNum(gte.value);
    var high = firstNum(lte.value);
    if (isNaN(low)) low = floor;
    if (isNaN(high)) high = ceiling;
    low = Math.min(Math.max(Math.floor(low), floor), ceiling);
    high = Math.min(Math.max(Math.ceil(high), floor), ceiling);
    if (low > high) low = high;

    /* a stale wrapper can only exist if a previous build was interrupted */
    var old = container.querySelector('.otx-range');
    if (old) old.parentNode.removeChild(old);

    var wrap = document.createElement('div');
    wrap.className = 'otx-range';
    wrap.innerHTML =
      '<div class="otx-range__readout"><span data-low></span><em>to</em><span data-high></span></div>' +
      '<div class="otx-range__track">' +
      '<span class="otx-range__rail"></span>' +
      '<span class="otx-range__fill"></span>' +
      '<span class="otx-range__thumb" data-role="low" tabindex="0" role="slider" aria-label="Minimum price"></span>' +
      '<span class="otx-range__thumb" data-role="high" tabindex="0" role="slider" aria-label="Maximum price"></span>' +
      '</div>';

    var track = wrap.querySelector('.otx-range__track');
    var fill = wrap.querySelector('.otx-range__fill');
    var thumbLow = wrap.querySelector('[data-role="low"]');
    var thumbHigh = wrap.querySelector('[data-role="high"]');
    var outLow = wrap.querySelector('[data-low]');
    var outHigh = wrap.querySelector('[data-high]');

    function money(v) {
      return symbol + Math.round(v).toLocaleString();
    }

    function pct(v) {
      return ((v - floor) / span) * 100;
    }

    function paint() {
      var a = pct(low);
      var b = pct(high);
      thumbLow.style.left = a + '%';
      thumbHigh.style.left = b + '%';
      fill.style.left = a + '%';
      fill.style.right = 100 - b + '%';
      outLow.textContent = money(low);
      outHigh.textContent = money(high);
      [[thumbLow, low], [thumbHigh, high]].forEach(function (pair) {
        pair[0].setAttribute('aria-valuemin', floor);
        pair[0].setAttribute('aria-valuemax', ceiling);
        pair[0].setAttribute('aria-valuenow', pair[1]);
        pair[0].setAttribute('aria-valuetext', money(pair[1]));
      });
    }

    /* An empty native input means "no bound set", which is what Dawn expects.
       Writing floor / ceiling explicitly would add a filter chip for a filter
       that is not narrowing anything. */
    function commit() {
      gte.value = low <= floor ? '' : String(low);
      lte.value = high >= ceiling ? '' : String(high);
      gte.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function valueAt(clientX) {
      var rect = track.getBoundingClientRect();
      if (!rect.width) return low;
      var t = (clientX - rect.left) / rect.width;
      t = Math.min(1, Math.max(0, t));
      return Math.round(floor + t * span);
    }

    function setValue(role, v) {
      v = Math.min(Math.max(v, floor), ceiling);
      if (role === 'low') low = Math.min(v, high);
      else high = Math.max(v, low);
      paint();
    }

    var active = null;

    function onDown(event) {
      var v = valueAt(event.clientX);
      /* grabbing the track jumps the nearer handle; grabbing a thumb takes it.
         When both sit on the same value, direction decides. */
      if (event.target === thumbLow) active = 'low';
      else if (event.target === thumbHigh) active = 'high';
      else if (low === high) active = v > high ? 'high' : 'low';
      else active = Math.abs(v - low) <= Math.abs(v - high) ? 'low' : 'high';

      wrap.setAttribute('data-dragging', '');
      setValue(active, v);
      if (track.setPointerCapture) {
        try {
          track.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
      event.preventDefault();
    }

    function onMove(event) {
      if (!active) return;
      setValue(active, valueAt(event.clientX));
      event.preventDefault();
    }

    function onUp(event) {
      if (!active) return;
      active = null;
      wrap.removeAttribute('data-dragging');
      if (track.releasePointerCapture) {
        try {
          track.releasePointerCapture(event.pointerId);
        } catch (e) {}
      }
      commit();
    }

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);

    [thumbLow, thumbHigh].forEach(function (thumb) {
      thumb.addEventListener('keydown', function (event) {
        var role = thumb.getAttribute('data-role');
        var current = role === 'low' ? low : high;
        var step = event.shiftKey ? 10 : 1;
        var next = null;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current - step;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current + step;
        else if (event.key === 'Home') next = floor;
        else if (event.key === 'End') next = ceiling;
        if (next === null) return;
        event.preventDefault();
        setValue(role, next);
        commit();
      });
    });

    paint();
    container.appendChild(wrap);
    container.setAttribute('data-otx-range', '');
  }

  function init() {
    injectStyles();
    var list = document.querySelectorAll('.facets__price');
    for (var i = 0; i < list.length; i++) build(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function () {
      window.clearTimeout(pending);
      pending = window.setTimeout(init, 0);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
